import React, { useState, useCallback, useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import PayrollTable from './components/PayrollTable/'
import HoursTable from './components/HoursTable/'
import BillingDetailsTable from './components/BillingDetailsTable/'
import Typography from '@mui/material/Typography'

import EditBillingDetailsDialog from './components/EditBillingDetailsDialog'

//hook
import usePayroll from './hooks/usePayroll'


//generate soas
import generate7starJasemSOA from './functions/generateSoa/7starjasem'
import generateSyzygySOA from './functions/generateSoa/syzygy'
import generate7starSOA from './functions/generateSoa/7star'


//payrolls
import printPayroll7star from './functions/generatePayroll/7star'
import printPayroll7starJasem from './functions/generatePayroll/7starjasem'
import printPayrollSyzygy from './functions/generatePayroll/syzygy'

import NoBillingDetailFound from './components/NoBIllingDetailFound'
import LoadingBillingDetails from './components/LoadingBillingDetails'

import BillingFigureCalculatorService from 'domain/billing/BillingFigureCalculatorService'
import BillingUtilities from 'domain/billing/BillingUtilities'

import BillingDetails from './components/BillingDetails'

//billing details generators
import generateBillingDetails from './functions/generateSoa/generateBillingDetails'

import { getPayrollAuthorities } from '../../../api/payroll'
import {
    getAllowanceColumnKeysFromPayroll,
    getPayrollPickerColumnTemplates,
    getPersonnelAllowanceNames,
} from './payrollAllowanceColumnKeys'

import Swal from 'sweetalert2'
import { useParams } from 'react-router-dom'


const ViewPayroll = () => {
    let { id } = useParams()
    let [billingDetailsDialogOpen, setBillingDialogDetailsVisibility] = useState(false)
    let {
        payroll,
        billingDetails,
        updateBillingDetails,
        status,
        payrollColumns,
        setNewBillingDetails,
        setPayslipHiddenAllowanceColumnKeys,
    } = usePayroll(id)

    const payrollRows = useMemo(() => payroll?.informations || [], [payroll?.informations])
    const allowanceColumnKeys = useMemo(
        () => getAllowanceColumnKeysFromPayroll(payroll),
        [payroll?.allowances]
    )
    const pickerColumnTemplates = useMemo(
        () => getPayrollPickerColumnTemplates(payrollColumns, payroll),
        [payrollColumns, payroll?.allowances]
    )
    const personnelAllowanceNames = useMemo(
        () => getPersonnelAllowanceNames(payroll),
        [payroll?.allowances]
    )
    const hiddenAllowanceColumnKeys = payroll?.payslip_hidden_allowance_column_keys || []

    const turnOnbillingDetails = useCallback(() => {
        setBillingDialogDetailsVisibility(true)
    }, [])

    const turnOffBillingDetails = useCallback(() => {
        setBillingDialogDetailsVisibility(false)
    }, [])

    const updateBilling = async (updates) => {
        await updateBillingDetails(updates)
        turnOffBillingDetails()
    }

    const onPayrollRowsUpdate = useCallback((updatedRows) => {
        billingDetails.gross_billing = BillingFigureCalculatorService.generateGrossBillingForThisCutoff(updatedRows || [])

        let updatedBilling = BillingFigureCalculatorService.computeBillingFigures(billingDetails)
        let finalBillingDetails = BillingUtilities.generateOtherDetailsBilling(updatedBilling)
        setNewBillingDetails(finalBillingDetails)
    }, [billingDetails])

    const printPayroll = useCallback(async () => {
        try {

            let payrollAuthoritiesRequest = await getPayrollAuthorities()

            if (payrollAuthoritiesRequest.message === 'No payroll authorities found')
                return Swal.fire({
                    title: 'Oops!',
                    icon: 'warning',
                    text: 'No payroll authorities found'
                })

            let payrollAuthorities = payrollAuthoritiesRequest.data

            if (!payrollAuthorities.payroll_head || !payrollAuthorities.payroll_manager || !payrollAuthorities.director_finance_dept || !payrollAuthorities.president) {
                return Swal.fire({
                    title: 'Oops!',
                    icon: 'warning',
                    text: 'Please set up your payroll authorities'
                })
            }

            switch (billingDetails.company) {
                case '7star':
                    return printPayroll7star(payrollAuthorities.payroll_head || 'no payroll head', payrollAuthorities.payroll_manager || 'no payroll manager', payrollAuthorities.director_finance_dept || 'no director finance dept', payrollAuthorities.president || 'no president', billingDetails.cutoff || 'cutoff', billingDetails.store, billingDetails.payroll_from || '', billingDetails.payroll_to)
                case '7starjasem':
                    return printPayroll7starJasem(payrollAuthorities.payroll_head || 'no payroll head', payrollAuthorities.payroll_manager || 'no payroll manager', payrollAuthorities.director_finance_dept || 'no president', billingDetails.cutoff || 'cutoff', billingDetails.store, billingDetails.payroll_from || '', billingDetails.payroll_to)
                case 'syzygy':
                    return printPayrollSyzygy(payrollAuthorities.payroll_head || 'no payroll head', payrollAuthorities.payroll_manager || 'no payroll manager', payrollAuthorities.director_finance_dept || 'no director finance dept', payrollAuthorities.president || 'no president', billingDetails.cutoff || 'cutoff', billingDetails.store, billingDetails.payroll_from || '', billingDetails.payroll_to)
            }

            Swal.fire({
                title: 'Oops!',
                icon: 'warning',
                text: 'No payroll found for this company'
            })
        } catch (err) {
            Swal.fire({
                title: 'Oops!',
                icon: 'warning',
                text: 'Cannot print payroll for now'
            })
        }
    }, [billingDetails])

    const printSOA = useCallback(async () => {

        let payrollAuthoritiesRequest = await getPayrollAuthorities()

        console.log({ payrollAuthoritiesRequest })

        if (payrollAuthoritiesRequest.message === 'No payroll authorities found')
            return Swal.fire({
                title: 'Oops!',
                icon: 'warning',
                text: 'No payroll authorities found'
            })

        let payrollAuthorities = payrollAuthoritiesRequest.data

        if (!payrollAuthorities.payroll_head || !payrollAuthorities.payroll_manager || !payrollAuthorities.director_finance_dept || !payrollAuthorities.president) {
            return Swal.fire({
                title: 'Oops!',
                icon: 'warning',
                text: 'Please set up your payroll authorities'
            })
        }

        billingDetails.payroll_head = payrollAuthorities.payroll_head || 'no payroll head'
        billingDetails.payroll_manager = payrollAuthorities.payroll_manager || 'no payroll manager'
        billingDetails.director_finance_dept = payrollAuthorities.director_finance_dept || 'no director finance dept'
        billingDetails.president = payrollAuthorities.president || 'no president'

        console.log('billingDetails', billingDetails)

        switch (billingDetails.company) {
            case '7star':
                return generate7starSOA(billingDetails)
            case '7starjasem':
                return generate7starJasemSOA(billingDetails)
            case 'syzygy':
                return generateSyzygySOA(billingDetails)
            default:
                Swal.fire({
                    title: 'Oops!',
                    icon: 'warning',
                    text: 'No SOA found for this company'
                })
        }
    }, [billingDetails])



    if (status === 'pending')
        return <div>Loading...</div>


    if (status === 'loading')
        return <LoadingBillingDetails />


    if (!billingDetails || status === 'error')
        return <NoBillingDetailFound />

    if (!billingDetails)
        return <NoBillingDetailFound />


    return <Stack spacing={2}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>{billingDetails.store} - {billingDetails.cutoff}</Typography>

        <Stack direction="row" spacing={2} >
            <Button sx={{
                bgcolor: '#00bcd4',
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 'medium',
                color: 'white',
                '&:hover': {
                    bgcolor: '#009bb3',
                },
                borderRadius: 1.5
            }} onClick={printPayroll}>Print Payroll</Button>
            <Button sx={{
                bgcolor: '#00bcd4',
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                color: 'white',
                fontWeight: 'medium',
                '&:hover': {
                    bgcolor: '#009bb3',
                },
                borderRadius: 1.5
            }} onClick={printSOA}>Print SOA</Button>
            <Button sx={{
                bgcolor: '#00bcd4',
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                color: 'white',
                fontWeight: 'medium',
                '&:hover': {
                    bgcolor: '#009bb3',
                },
                borderRadius: 1.5
            }} onClick={turnOnbillingDetails}>Edit Billing Details</Button>
        </Stack>


        <HoursTable company={billingDetails.company} data={payroll.informations || []} store={billingDetails.store || ""} />
        <PayrollTable
            payrollColumns={payrollColumns}
            pickerColumnTemplates={pickerColumnTemplates}
            personnelAllowanceNames={personnelAllowanceNames}
            company={billingDetails.company}
            data={payrollRows}
            store={billingDetails.store || ""}
            onPayrollRowsUpdate={onPayrollRowsUpdate}
            allowanceColumnKeys={allowanceColumnKeys}
            hiddenAllowanceColumnKeys={hiddenAllowanceColumnKeys}
            onHiddenAllowancePersisted={setPayslipHiddenAllowanceColumnKeys}
        />
        <BillingDetailsTable company={billingDetails.company} data={billingDetails} allowances={(payroll.itemizedAllowances || [])} />
        {/*<BillingDetails company={billingDetails.data.company} data={generatedBillingDetails} />

        {/**for editing billing details */}
        <EditBillingDetailsDialog company={billingDetails.company} billingDetails={billingDetails} open={billingDetailsDialogOpen} onClose={turnOffBillingDetails} onUpdate={updateBilling} />
    </Stack>
}

export default ViewPayroll